import { getLocationTypeForValue, type StoredLocationTypes } from '@commander/utils';
import type { LocationType, OnboardingFormData, UserRole } from '../types/onboarding.types';
import { COMMUNICATION_STYLES, DOMAIN_REGEX, EMAIL_REGEX } from './constants';

/**
 * Utility functions for onboarding data manipulation and validation
 */

/**
 * Formats locations array to LocationType array.
 *
 * `storedTypes` carries the types the ICP's locations were saved with so a
 * value the user did not touch keeps its type; only fresh values are inferred.
 */
export const formatLocationsForAPI = (
  locations: string[],
  storedTypes: StoredLocationTypes
): LocationType[] => {
  return locations
    .filter((item) => item.trim() !== '')
    .map((value: string) => ({
      type: getLocationTypeForValue(value, storedTypes),
      value,
    }));
};

/**
 * Validates and formats domain input
 */
export const formatDomain = (domain: string): string => {
  const trimmed = domain.trim();

  // Add https:// if no protocol is specified
  if (trimmed && !trimmed.match(/^https?:\/\//) && !trimmed.match(/^localhost/)) {
    return `https://${trimmed}`;
  }

  return trimmed;
};

/**
 * Checks if a domain is valid
 */
export const isValidDomain = (domain: string): boolean => {
  if (!domain.trim()) return false;
  return DOMAIN_REGEX.test(domain.trim());
};

/**
 * Validates an email address
 */
export const isValidEmail = (email: string): boolean => {
  if (!email.trim()) return false;
  return EMAIL_REGEX.test(email.trim());
};

/**
 * Converts JSON size range to display format
 */
export const convertJSONToSizeRange = (sizeRange: any): string | null => {
  if (!sizeRange || typeof sizeRange !== 'object') {
    return null;
  }

  // Based on the original conversion logic
  if (sizeRange.min === 1 && sizeRange.max === 5) return '1-5';
  if (sizeRange.min === 5 && sizeRange.max === 30) return '5-30';
  if (sizeRange.min === 30) return '30+';

  // For other ranges, format as min-max or min+
  if (sizeRange.min && sizeRange.max) {
    return `${sizeRange.min}-${sizeRange.max}`;
  }
  if (sizeRange.min) {
    return `${sizeRange.min}+`;
  }

  return null;
};

interface SizeRange {
  min_employees: number;
  max_employees?: number;
}

interface RangeMapping {
  [key: string]: SizeRange;
}

/**
 * Converts display size range to JSON format for API
 * Supports generic formats like "1-5", "5-30", "30+", "100-200", "500+", etc.
 */
export const convertSizeRangeToJSON = (
  sizeRange: string,
  customRanges?: RangeMapping
): SizeRange | null => {
  if (!sizeRange || typeof sizeRange !== 'string') {
    return null;
  }

  const trimmedRange = sizeRange.trim();

  // Check custom ranges first
  if (customRanges?.[trimmedRange]) {
    return customRanges[trimmedRange];
  }

  // Default ranges for backward compatibility
  const defaultRanges: RangeMapping = {
    '1-5': { min_employees: 1, max_employees: 5 },
    '5-30': { min_employees: 5, max_employees: 30 },
    '30+': { min_employees: 30 },
  };

  // Check default ranges
  if (defaultRanges[trimmedRange]) {
    return defaultRanges[trimmedRange];
  }

  // Parse generic range formats
  // Format: "min-max" (e.g., "10-50", "100-200")
  const rangeMatch = trimmedRange.match(/^(\d+)-(\d+)$/);
  if (rangeMatch?.[1] && rangeMatch[2]) {
    const min = parseInt(rangeMatch[1], 10);
    const max = parseInt(rangeMatch[2], 10);

    if (min >= 0 && max >= min) {
      return { min_employees: min, max_employees: max };
    }
  }

  // Format: "min+" (e.g., "50+", "100+")
  const minPlusMatch = trimmedRange.match(/^(\d+)\+$/);
  if (minPlusMatch?.[1]) {
    const min = parseInt(minPlusMatch[1], 10);

    if (min >= 0) {
      return { min_employees: min };
    }
  }

  // Format: single number (e.g., "25", "100")
  const singleNumberMatch = trimmedRange.match(/^(\d+)$/);
  if (singleNumberMatch?.[1]) {
    const value = parseInt(singleNumberMatch[1], 10);

    if (value >= 0) {
      return { min_employees: value, max_employees: value };
    }
  }

  return null;
};

/**
 * Gets communication style data by style ID
 */
export const getCommunicationStyleData = (styleId: string) => {
  return COMMUNICATION_STYLES.find(
    (styleData) => styleData.style.toLowerCase() === styleId.toLowerCase()
  );
};

/**
 * Format invite user data for API submission
 */
export const formatInviteUsersForAPI = (
  inviteUsers: Array<{ fullName: string; email: string }>
) => {
  return {
    employees: inviteUsers.map((user) => ({
      name: user.fullName,
      email: user.email,
    })),
  };
};

/**
 * Gets the step list based on user role
 */
export const getStepListForRole = (role: UserRole | null): string[] => {
  if (role === 'admin') {
    return [
      'Company',
      'Locations',
      'Services',
      'Size',
      'Industries',
      'Job Titles',
      'Email Setting',
      'Integrations',
      'Invite',
    ];
  }

  return [
    'Company',
    'Locations',
    'Services',
    'Size',
    'Industries',
    'Job Titles',
    'Email Setting',
    'Integrations',
  ];
};

/**
 * Validates if user is an admin based on role-based company employee data
 */
export const validateUserRole = (user: any): UserRole => {
  if (user?.company_employee?.role === 'ADMIN' && user?.company_employee?.status === 'JOINED') {
    return 'admin';
  }

  return 'salesperson';
};

/**
 * Debounce utility function
 */
export const createDebounce = <T extends (...args: any[]) => any>(
  func: T,
  delay: number
): ((...args: Parameters<T>) => void) & { cancel: () => void } => {
  let timeoutId: NodeJS.Timeout | null = null;

  const debouncedFn = (...args: Parameters<T>) => {
    // Clear any existing timeout before setting a new one
    if (timeoutId !== null) {
      clearTimeout(timeoutId);
    }

    // Set new timeout
    timeoutId = setTimeout(() => func.apply(null, args), delay);
  };

  // Add cancel method to clear the pending timeout
  debouncedFn.cancel = () => {
    if (timeoutId !== null) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
  };

  return debouncedFn;
};

/**
 * Creates deep copy of object
 */
export const deepClone = <T>(obj: T): T => {
  return JSON.parse(JSON.stringify(obj));
};

/**
 * Checks if two arrays have the same content regardless of order
 */
export const arraysEqual = <T>(arr1: T[], arr2: T[]): boolean => {
  if (arr1.length !== arr2.length) return false;

  const sorted1 = [...arr1].sort();
  const sorted2 = [...arr2].sort();

  return sorted1.every((item, index) => item === sorted2[index]);
};

/**
 * Removes duplicate items from array while preserving order
 */
export const removeDuplicates = <T>(array: T[]): T[] => {
  const seen = new Set<T>();
  const result: T[] = [];

  for (const item of array) {
    if (!seen.has(item)) {
      seen.add(item);
      result.push(item);
    }
  }

  return result;
};

/**
 * Sanitizes string input
 */
export const sanitizeString = (str: string): string => {
  return str.trim().replace(/\s+/g, ' ');
};

/**
 * Checks if a step should be shown based on user role and trial status
 */
export const shouldShowStep = (
  stepId: string,
  userRole: UserRole | null,
  isTrial = false
): boolean => {
  // Invite step only for admin users
  if (stepId === 'invite') {
    return userRole === 'admin';
  }

  // Trials sign in with email + password only and can't connect integrations,
  // so skip the integrations step for them entirely.
  if (stepId === 'integrations') {
    return !isTrial;
  }

  return true;
};

/**
 * Gets step progress percentage
 */
export const getStepProgress = (currentStep: number, totalSteps: number): number => {
  return Math.round((currentStep / totalSteps) * 100);
};

/**
 * Formats file size for display
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / k ** i).toFixed(2))} ${sizes[i]}`;
};

/**
 * Generates a unique ID for DOM elements
 */
export const generateId = (prefix: string = 'id'): string => {
  return `${prefix}-${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Checks if a value is empty or undefined
 */
export const isEmpty = (value: any): boolean => {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.trim().length === 0;
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'object') return Object.keys(value).length === 0;
  return false;
};

/**
 * Gets the first error message from an object
 */
export const getFirstError = (errors: Record<string, string>): string | undefined => {
  const firstKey = Object.keys(errors)[0];
  return firstKey ? errors[firstKey] : undefined;
};

/**
 * Parses JSON safely
 */
export const safeJSONParse = <T>(str: string, fallback: T): T => {
  try {
    return JSON.parse(str);
  } catch {
    return fallback;
  }
};

/**
 * Converts string to boolean safely
 */
export const safeStringToBool = (str: string | undefined | null): boolean => {
  if (!str) return false;
  return ['true', '1', 'yes', 'on'].includes(str.toLowerCase());
};

/**
 * Converts array of size objects to display format (e.g., "1-5", "1-200")
 */
export const convertSizeObjectsToRanges = (
  sizes: Array<{ min_employees?: number; max_employees?: number }>
): string[] => {
  if (!Array.isArray(sizes) || sizes.length === 0) {
    return [];
  }

  return sizes
    .filter(
      (size) => size && (size.min_employees !== undefined || size.max_employees !== undefined)
    )
    .map((size) => {
      const min = size.min_employees || 1;
      const max = size.max_employees;

      if (max) {
        return `${min}-${max}`;
      } else {
        return `${min}+`;
      }
    });
};

/**
 * Helper function to save step data using the appropriate service method
 * Consolidates the switch statement logic used across multiple hooks
 */
export const saveStepData = async (
  service: any, // Using any to avoid circular imports - the service has the expected methods
  data: OnboardingFormData,
  stepId: string,
  context: string = 'save',
  existingProfiles?: { companyProfile?: any; companyICP?: any }
): Promise<any> => {
  switch (stepId) {
    case 'company':
      return service.saveCompanyInfo(data, existingProfiles?.companyProfile);

    case 'services':
      // Pass company profile data if available, otherwise require profile data from form data
      return service.saveServices(data.services, existingProfiles?.companyProfile, {
        company_name: data.company,
        domain: data.domain,
      });

    case 'locations':
      return service.saveLocations(data.locations, existingProfiles?.companyICP);

    case 'size':
      return service.saveCompanySize(data.size, existingProfiles?.companyICP);

    case 'industries':
      return service.saveIndustries(data.industries, existingProfiles?.companyICP);

    case 'jobTitles':
      return service.saveJobTitles(data.jobTitles, existingProfiles?.companyICP);

    case 'emailSettings':
      return service.saveEmailSettings(data.emailSettings);

    case 'invite':
      return service.saveInviteUsers(data.inviteUsers);

    default:
      console.warn(`Unknown step for ${context}: ${stepId}`);
      return null;
  }
};
